import React from "react";
import { Alert, Divider, Paper, Step, StepLabel, Stepper } from "@mui/material";
import PageShell from "../components/PageShell";

import { normalizeList, fkId, sameId } from "../utils/coreHelpers";
import {
  listHospitals,
  listWards,
  listRooms,
  listBeds,
  listDepartments,
  getUsers,
} from "../api/coreApi";

import HospitalFormStep from "../forms/HospitalFormStep";
import WardFormStep from "../forms/WardFormStep";
import RoomFormStep from "../forms/RoomFormStep";
import BedFormStep from "../forms/BedFormStep";
import DepartmentFormStep from "../forms/DepartmentFormStep";
import UserFormStep from "../forms/UserFormStep";

const STEPS = ["Hospital", "Ward", "Room", "Bed", "Department", "User/Staff"];

export default function CoreWizardPage() {
  const [step, setStep] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");

  // ✅ Final success after last step
  const [finalSuccess, setFinalSuccess] = React.useState(false);

  // master lists
  const [hospitals, setHospitals] = React.useState([]);
  const [wards, setWards] = React.useState([]);
  const [rooms, setRooms] = React.useState([]);
  const [beds, setBeds] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [users, setUsers] = React.useState([]);

  // selected (linked)
  const [selectedHospitalId, _setSelectedHospitalId] = React.useState(
    localStorage.getItem("hospital_id") || ""
  );
  const [selectedWardId, setSelectedWardId] = React.useState("");
  const [selectedRoomId, setSelectedRoomId] = React.useState("");

  const setSelectedHospitalId = (id) => {
    const v = id || "";
    _setSelectedHospitalId(v);
    localStorage.setItem("hospital_id", v);

    // reset downstream links
    setSelectedWardId("");
    setSelectedRoomId("");
  };

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const finishWizard = () => {
    setFinalSuccess(true);
  };

  const refreshAll = async () => {
    try {
      const [hs, ws, rs, bs, ds, us] = await Promise.all([
        listHospitals(),
        listWards(),
        listRooms(),
        listBeds(),
        listDepartments(),
        getUsers(),
      ]);

      setHospitals(normalizeList(hs.data));
      setWards(normalizeList(ws.data));
      setRooms(normalizeList(rs.data));
      setBeds(normalizeList(bs.data));
      setDepartments(normalizeList(ds.data));
      setUsers(normalizeList(us.data));
    } catch (e) {
      setErr(
        e?.response?.data ? JSON.stringify(e.response.data) : "Failed to refresh lists"
      );
    }
  };

  React.useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep selected hospital valid
  React.useEffect(() => {
    if (!selectedHospitalId) return;
    const exists = hospitals.some((h) =>
      sameId(fkId(h.hospital_id || h.id), selectedHospitalId)
    );
    if (hospitals.length && !exists) {
      setSelectedHospitalId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitals]);

  // ---- filtered linked lists (by selection) ----
  const wardsOfHospital = wards.filter((w) =>
    sameId(fkId(w.organization || w.hospital || w.organization_id), selectedHospitalId)
  );

  const roomsOfWard = rooms.filter((r) =>
    sameId(fkId(r.ward || r.ward_id), selectedWardId)
  );

  const bedsOfWard = beds.filter((b) =>
    sameId(fkId(b.ward || b.ward_id), selectedWardId)
  );

  const deptsOfHospital = departments.filter((d) =>
    sameId(fkId(d.organization || d.hospital || d.organization_id), selectedHospitalId)
  );

  const usersOfHospital = users.filter((u) =>
    sameId(fkId(u.organization || u.hospital || u.organization_id), selectedHospitalId)
  );

  // Common props to steps
  const common = {
    step,
    setStep,
    goNext,
    goBack,

    loading,
    setLoading,

    err,
    setErr,

    refreshAll,

    hospitals,
    wards: wardsOfHospital,
    rooms: roomsOfWard,
    beds: bedsOfWard,
    departments: deptsOfHospital,
    users: usersOfHospital,

    selectedHospitalId,
    setSelectedHospitalId,
    selectedWardId,
    setSelectedWardId,
    selectedRoomId,
    setSelectedRoomId,
  };

  const content = [
    <HospitalFormStep key="h" {...common} />,
    <WardFormStep key="w" {...common} />,
    <RoomFormStep key="r" {...common} />,
    <BedFormStep key="b" {...common} />,
    <DepartmentFormStep key="d" {...common} />,
    <UserFormStep key="u" {...common} onFinish={finishWizard} />,
  ][step];

  return (
    <PageShell
      title="Core Setup Wizard"
      subtitle="Hospital → Ward → Room → Bed → Department → User"
    >
      {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}

      {/* ✅ Final success message */}
      {finalSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Setup completed successfully ✅ Now you can go to Lists page and manage data.
        </Alert>
      ) : null}

      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((s) => (
            <Step key={s}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ my: 2 }} />

        {content}
      </Paper>
    </PageShell>
  );
}